import { pxToRem } from '@modernhealth/vital-components';

import * as Sentry from '@sentry/browser';
import React, { FC } from 'react';

import styled from '@emotion/styled';
import { Markdown } from 'src/modules/newPrograms/components/Markdown';
import { Page, PageProps } from 'src/modules/newPrograms/components/Page';
import { ProgramPageFragment } from 'src/modules/newPrograms/documents/getProgram.generated';
import {
    VideoJSTextTrack,
    VideoPlayer,
    VideoPlayerTextTrack,
    VideoPlayerSupportedFileFormats,
} from 'src/shared/components/VideoPlayer';
import { mediaQueries } from 'src/shared/styles/variables';
import { resolveTextTrack } from 'src/shared/helpers/textTrack';

const Container = styled.div({
    display: 'flex',
    alignItems: 'center',
    [mediaQueries.md]: {
        flexWrap: 'wrap',
    },
    flexDirection: 'column-reverse',
    justifyContent: 'center',
});

const MarkdownContainer = styled.div({
    maxWidth: pxToRem(700),
    width: pxToRem(700),
});

const VideoContainer = styled.div({
    marginBottom: pxToRem(30),
});

interface VideoPageProps extends PageProps {
    page: ProgramPageFragment;
}

export const VideoPageInternalErrorText =
    "The content on this page is misconfigured. We've been alerted and we're working on fixing it! In the meantime, you can skip this video and come back to it later once we've fixed it!";

const ErrorContainer = () => {
    return <div>{VideoPageInternalErrorText}</div>;
};

type VideoPageWrapperProps = Omit<PageProps, 'justifyNavigation'>;

const VideoPageWrapper: FC<VideoPageWrapperProps> = ({
    onNavigatePrev,
    onNavigateNext,
    contentVerticalAlignment,
    navigationComponent,
    children,
}) => {
    return (
        <Page
            onNavigatePrev={onNavigatePrev}
            onNavigateNext={onNavigateNext}
            contentVerticalAlignment={contentVerticalAlignment}
            navigationComponent={navigationComponent}
            justifyNavigation="center"
        >
            <Container>
                <MarkdownContainer>{children}</MarkdownContainer>
            </Container>
        </Page>
    );
};

export const VideoPage: FC<VideoPageProps> = ({
    page,
    onNavigatePrev,
    onNavigateNext,
    navigationComponent,
}) => {
    if (
        !page.videoContent ||
        (!page.videoContent.textTrack && !page.videoContent.textTrackTranslation) ||
        !page.videoContent.poster
    ) {
        Sentry.withScope(scope => {
            scope.setExtras({
                videoContentID: page.videoContent?.id,
                poster: page.videoContent?.poster,
                source: page.videoContent?.sourceHorizontal,
                textTrack: page.videoContent?.textTrack,
                textTrackTranslation: page.videoContent?.textTrackTranslation,
            });
            Sentry.captureException('VideoContent is misconfigured');
        });
        return (
            <VideoPageWrapper
                onNavigateNext={onNavigateNext}
                onNavigatePrev={onNavigatePrev}
                contentVerticalAlignment={page.contentVerticalAlignment}
                navigationComponent={navigationComponent}
            >
                <ErrorContainer />
            </VideoPageWrapper>
        );
    }
    const source = {
        src: page.videoContent.sourceHorizontal,
        type: VideoPlayerSupportedFileFormats.MP4,
    };
    const poster = page.videoContent.poster;
    const textTrack = resolveTextTrack(
        page.id,
        page.videoContent.textTrack,
        page.videoContent.textTrackTranslation,
    );
    const textTracks: VideoJSTextTrack[] | undefined = textTrack ? [textTrack] : undefined;

    return (
        <VideoPageWrapper
            onNavigateNext={onNavigateNext}
            onNavigatePrev={onNavigatePrev}
            contentVerticalAlignment={page.contentVerticalAlignment}
            navigationComponent={navigationComponent}
        >
            <VideoContainer>
                <VideoPlayer source={source} poster={poster} title={page.videoContent.title}>
                    {textTracks?.map(track => {
                        return <VideoPlayerTextTrack key={track.srcLang} track={track} />;
                    })}
                </VideoPlayer>
            </VideoContainer>
            <Markdown
                textHorizontalAlignment={page.contentHorizontalAlignment}
                text={page.content ?? ''}
            />
        </VideoPageWrapper>
    );
};
